import type {
  FilterSpecification,
  GeoJSONSource,
  Map as MapLibreMap,
  MapGeoJSONFeature,
} from "maplibre-gl";
import type { Theme } from "./theme";
import type { FeatureCollection, NodeFeature } from "./types";

const SOURCE_ID = "meshatlas-nodes";
const CIRCLE_LAYER_ID = "meshatlas-nodes-circle";
const LABEL_LAYER_ID = "meshatlas-nodes-label";

const FRESH_SECONDS = 15 * 60; // seen in the last 15 min
const STALE_SECONDS = 60 * 60; // seen in the last hour

export type NodeStatus = "fresh" | "stale" | "offline";

export class NodesLayer {
  private readonly map: MapLibreMap;
  private readonly nodes = new Map<string, NodeFeature>();
  private theme: Theme;
  private onSelect: ((feature: NodeFeature) => void) | null = null;
  private onHover: ((feature: NodeFeature | null) => void) | null = null;
  private hoveredId: string | null = null;
  private refreshTimer: number | undefined;

  // null = "no restriction"; all three default to showing everything.
  private visibleSystems: Set<string> | null = null;
  private visibleStatuses: Set<NodeStatus> = new Set(["fresh", "stale", "offline"]);
  private visibleRegion: string | null = null;

  constructor(map: MapLibreMap, theme: Theme) {
    this.map = map;
    this.theme = theme;
    this.attachToStyle();

    this.map.on("click", CIRCLE_LAYER_ID, (event) => {
      const feature = event.features?.[0] as MapGeoJSONFeature | undefined;
      if (feature) this.onSelect?.(featureFromMapFeature(feature));
    });
    this.map.on("mouseenter", CIRCLE_LAYER_ID, () => {
      this.map.getCanvas().style.cursor = "pointer";
    });
    this.map.on("mouseleave", CIRCLE_LAYER_ID, () => {
      this.map.getCanvas().style.cursor = "";
      if (this.hoveredId !== null) {
        this.hoveredId = null;
        this.onHover?.(null);
      }
    });
    // "mouseenter"/"mouseleave" on a layer only fire once for the whole
    // layer (entering/leaving *a* circle, not switching between two
    // adjacent ones), so per-feature hover needs "mousemove" instead,
    // comparing against the last-hovered id to only fire onHover when it
    // actually changes.
    this.map.on("mousemove", CIRCLE_LAYER_ID, (event) => {
      const feature = event.features?.[0] as MapGeoJSONFeature | undefined;
      const id = (feature?.properties.native_id as string | undefined) ?? null;
      if (id === this.hoveredId) return;
      this.hoveredId = id;
      this.onHover?.(feature ? featureFromMapFeature(feature) : null);
    });

    this.refreshTimer = window.setInterval(() => this.render(), 30_000);
  }

  /** Adds our source and both layers to whatever style is currently
   * active. Split out from the constructor so setMapTheme()'s style swap
   * (which wipes every programmatically added source/layer -- see
   * mapSetup.ts's setMapTheme() comment) can re-run just this part via
   * reattach(), without re-registering the click/hover listeners below
   * (those are bound to the Map instance by layer id and survive the
   * layer being removed and re-added with the same id). */
  private attachToStyle(): void {
    this.map.addSource(SOURCE_ID, {
      type: "geojson",
      // See the cast in render() for why this needs `unknown` in between:
      // our NodeFeature type allows a null geometry, maplibre's stricter
      // GeoJSON type doesn't, and an empty features array trivially
      // satisfies both at runtime.
      data: emptyCollection() as unknown as GeoJSON.FeatureCollection,
    });

    // Stroke/label colors are near-black/near-white, tuned for contrast
    // against one specific basemap brightness -- they need to flip per
    // theme or nodes/labels wash out against the other basemap. Status
    // fill colors stay theme-invariant (same reasoning as the CSS
    // swatches in style.css).
    const strokeColor = this.theme === "day" ? "#ffffff" : "#0d1117";
    const textColor = this.theme === "day" ? "#0d1117" : "#e6edf3";

    this.map.addLayer({
      id: CIRCLE_LAYER_ID,
      type: "circle",
      source: SOURCE_ID,
      filter: ["==", ["geometry-type"], "Point"],
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 2, 3, 12, 8],
        "circle-color": [
          "match",
          ["get", "status"],
          "fresh",
          "#3ecf6a",
          "stale",
          "#e0a63c",
          /* offline */ "#6b7684",
        ],
        "circle-stroke-color": strokeColor,
        "circle-stroke-width": 1.5,
      },
    });

    this.map.addLayer({
      id: LABEL_LAYER_ID,
      type: "symbol",
      source: SOURCE_ID,
      filter: ["==", ["geometry-type"], "Point"],
      layout: {
        "text-field": ["coalesce", ["get", "short_name"], ["get", "native_id"]],
        "text-size": 11,
        "text-offset": [0, 1.2],
        "text-anchor": "top",
        "text-optional": true,
      },
      paint: {
        "text-color": textColor,
        "text-halo-color": strokeColor,
        "text-halo-width": 1,
      },
    });
  }

  /** Called after a basemap theme switch (see mapSetup.ts's setMapTheme()):
   * re-adds our source/layers (wiped by the style swap) with colors for
   * the new theme, then repopulates the new source from `this.nodes`,
   * which is untouched by any of this -- it lives in this class, not in
   * the style. */
  reattach(theme: Theme): void {
    this.theme = theme;
    this.attachToStyle();
    this.render();
  }

  onNodeClick(handler: (feature: NodeFeature) => void): void {
    this.onSelect = handler;
  }

  /** Fires with the hovered node's feature on entry, and with `null` when
   * the pointer leaves it (whether onto empty map or straight onto a
   * different node -- see the mousemove/mouseleave wiring above). */
  onNodeHover(handler: (feature: NodeFeature | null) => void): void {
    this.onHover = handler;
  }

  /** `null` clears the restriction (show every system). Purely a display
   * filter -- it doesn't affect what's fetched/stored, so toggling it
   * back off is instant with no refetch. */
  setSystemFilter(systemIds: string[] | null): void {
    this.visibleSystems = systemIds ? new Set(systemIds) : null;
    this.applyFilter();
  }

  setStatusFilter(statuses: NodeStatus[]): void {
    this.visibleStatuses = new Set(statuses);
    this.applyFilter();
  }

  /** `null` clears the restriction (show every region). */
  setRegionFilter(region: string | null): void {
    this.visibleRegion = region;
    this.applyFilter();
  }

  /** Distinct, non-null `region` values across every currently loaded
   * node, sorted -- used to populate the region filter dropdown. */
  availableRegions(): string[] {
    const regions = new Set<string>();
    for (const feature of this.nodes.values()) {
      if (feature.properties.region) regions.add(feature.properties.region);
    }
    return [...regions].sort();
  }

  setAll(collection: FeatureCollection<NodeFeature>): void {
    this.nodes.clear();
    for (const feature of collection.features) {
      this.nodes.set(feature.properties.id, feature);
    }
    this.render();
  }

  upsert(feature: NodeFeature): void {
    this.nodes.set(feature.properties.id, feature);
    this.render();
  }

  get(nodeId: string): NodeFeature | undefined {
    return this.nodes.get(nodeId);
  }

  all(): NodeFeature[] {
    return [...this.nodes.values()];
  }

  /** Nodes currently passing every visibility filter (system/status/
   * region) -- i.e. the ones actually drawn on the map right now. Used by
   * TourMode so it only tours what's on screen rather than the full
   * unfiltered dataset. */
  visible(): NodeFeature[] {
    const now = Date.now();
    return [...this.nodes.values()].filter((feature) => {
      if (!hasGeometry(feature)) return false;
      if (this.visibleSystems && !this.visibleSystems.has(feature.properties.system_id)) return false;
      if (!this.visibleStatuses.has(statusFor(feature, now))) return false;
      if (this.visibleRegion && feature.properties.region !== this.visibleRegion) return false;
      return true;
    });
  }

  findByNativeId(nativeId: string): NodeFeature | undefined {
    for (const feature of this.nodes.values()) {
      if (feature.properties.native_id === nativeId) return feature;
    }
    return undefined;
  }

  destroy(): void {
    window.clearInterval(this.refreshTimer);
  }

  private render(): void {
    const now = Date.now();
    // Nodes without a known position can't be placed on the map; they
    // still exist in `this.nodes` (e.g. for the sidebar/history lookup)
    // but are excluded from what we hand to the GeoJSON source.
    const features = [...this.nodes.values()]
      .filter(hasGeometry)
      .map((f) => ({
        ...f,
        properties: { ...f.properties, status: statusFor(f, now) },
      }));
    const source = this.map.getSource(SOURCE_ID);
    if (source && "setData" in source) {
      // Our NodeFeature type allows `geometry: null` (a node with no
      // position yet); the filter above guarantees every feature here has
      // one, but expressing that as a type guard for maplibre's stricter
      // GeoJSON.FeatureCollection type isn't worth the ceremony -- cast.
      (source as GeoJSONSource).setData({
        type: "FeatureCollection",
        features,
      } as unknown as GeoJSON.FeatureCollection);
    }
    this.applyFilter();
  }

  /** Recomputes the combined MapLibre `filter` expression from
   * `visibleSystems` + `visibleStatuses` and applies it to both layers.
   * Called after every render() (status is recomputed there) and
   * whenever a filter setter runs. */
  private applyFilter(): void {
    const clauses: unknown[] = [["==", ["geometry-type"], "Point"]];
    if (this.visibleSystems) {
      clauses.push(["in", ["get", "system_id"], ["literal", [...this.visibleSystems]]]);
    }
    clauses.push(["in", ["get", "status"], ["literal", [...this.visibleStatuses]]]);
    if (this.visibleRegion) {
      clauses.push(["==", ["get", "region"], this.visibleRegion]);
    }

    const filter = ["all", ...clauses] as unknown as FilterSpecification;
    if (this.map.getLayer(CIRCLE_LAYER_ID)) this.map.setFilter(CIRCLE_LAYER_ID, filter);
    if (this.map.getLayer(LABEL_LAYER_ID)) this.map.setFilter(LABEL_LAYER_ID, filter);
  }
}

function hasGeometry(feature: NodeFeature): feature is NodeFeature & { geometry: NonNullable<NodeFeature["geometry"]> } {
  return feature.geometry !== null;
}

function statusFor(feature: NodeFeature, nowMs: number): NodeStatus {
  const lastSeen = feature.properties.last_seen;
  if (!lastSeen) return "offline";
  const ageSeconds = (nowMs - new Date(lastSeen).getTime()) / 1000;
  if (ageSeconds <= FRESH_SECONDS) return "fresh";
  if (ageSeconds <= STALE_SECONDS) return "stale";
  return "offline";
}

function emptyCollection(): FeatureCollection<NodeFeature> {
  return { type: "FeatureCollection", features: [] };
}

function featureFromMapFeature(feature: MapGeoJSONFeature): NodeFeature {
  return {
    type: "Feature",
    geometry: feature.geometry as NodeFeature["geometry"],
    properties: feature.properties as NodeFeature["properties"],
  };
}
